/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { closeModal, closeSecondaryModal } from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import { Offer } from 'types/entities/Offer'
import { OffersAction } from 'store/offers/OffersAction'
import { toast } from 'utils/toastUtils'
import UiCounter from 'components/Ui/UiCounter'
import UiFormLabel from 'components/Ui/UiFormLabel'

interface EditOfferPersonsCountModalProps {
    offer: Offer
    onSuccess?: () => void
    isSecondary?: boolean
}

const EditOfferPersonsCountModal: React.FC<EditOfferPersonsCountModalProps> = ({
    offer,
    onSuccess,
    isSecondary = false,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [adultsCount, setAdultsCount] = useState<number>(
        offer.packagesCart?.drivers_count_adult ?? 0
    )
    const [childrenCount, setChildrenCount] = useState<number>(
        offer.packagesCart?.drivers_count_child ?? 0
    )
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSave = async (closeAfterSave: boolean = true) => {
        setIsSubmitting(true)
        try {
            if (!offer.packagesCart) {
                toast.error(t('No packages cart found'))
                setIsSubmitting(false)
                return
            }

            await dispatch(
                OffersAction.setOfferPersonsCount({
                    packages_cart_id: offer.packagesCart.id,
                    drivers_count_adult: adultsCount,
                    drivers_count_child: childrenCount,
                })
            )

            toast.success(t('Persons count updated'))
            if (closeAfterSave) {
                if (isSecondary) {
                    dispatch(closeSecondaryModal())
                } else {
                    dispatch(closeModal())
                }
            } else {
                setIsSubmitting(false)
            }
            if (onSuccess) onSuccess()
        } catch (e: any) {
            console.error(e)
            toast.error(t('Failed to update persons count'))
            setIsSubmitting(false)
        }
    }

    return (
        <UiFormModalShell
            className="EditOfferPersonsCountModal"
            title={t('Edit Persons count')}
            showHeader
            footer={
                <UiFormActionsBar
                    primaryText={t('Save')}
                    onPrimary={() => handleSave(false)}
                    saveAndCloseText={t('Save and close')}
                    onSaveAndClose={() => handleSave(true)}
                    loading={isSubmitting}
                />
            }
        >
            <div className="p-4 flex flex-col gap-6">
                <div className="flex gap-6">
                    <div className="flex flex-col gap-3 flex-1">
                        <UiFormLabel>{t('Adults')}</UiFormLabel>
                        <UiCounter
                            value={adultsCount}
                            onChange={(value) => setAdultsCount(typeof value === 'number' ? value : 0)}
                            min={0}
                            step={1}
                        />
                    </div>

                    <div className="flex flex-col gap-3 flex-1">
                        <UiFormLabel>{t('Children')}</UiFormLabel>
                        <UiCounter
                            value={childrenCount}
                            onChange={(value) => setChildrenCount(typeof value === 'number' ? value : 0)}
                            min={0}
                            step={1}
                        />
                    </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
                    <div className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                        {t('Total persons')}: {adultsCount + childrenCount}
                    </div>
                </div>
            </div>
        </UiFormModalShell>
    )
}

export default EditOfferPersonsCountModal
